import { computed, effect, inject, Injectable, Signal, signal } from '@angular/core';
import { FormControl } from '@angular/forms';
import { TranslocoService } from '@jsverse/transloco';
import { firstValueFrom } from 'rxjs';

import { ProjectBloc } from '@connect-services/client/blocs/project.bloc';
import { AddLayerFluxService } from '@connect-services/client/services/add-layer-flux.service';
import { FieldInterventionPermissionService } from '@connect-services/client/services/field-intervention-permission.service';
import {
    CreateLayerDtoWithoutProject,
    LayerCreationFormService,
    LayerFormRecord,
} from '@connect-services/client/services/layer-creation-form.service';
import { UrlSourceEnum, UrlSourceService } from '@connect-services/client/services/url-source.service';
import { CheckboxState } from '@connect-services/src/lib/components/checkbox/checkbox.component';
import { GroupBucket, SelectOption } from '@connect-services/src/lib/components/select/select.component';
import { NO_GROUP } from '@connect-services/src/lib/components/select-by-group/select-by-group.component';
import {
    DepositFolder,
    Interstandard,
    Layer,
    LayerPreset,
    LayerPresetsService,
    LayersService,
    Standard,
    TablesService,
} from '@connect-services/src/sdk/api';
import { defaultPaginationParameters, paginationMaxTake } from '@shared/client/models/pagination.model';

export type { ServiceCatalogResolutionError } from '@connect-services/client/services/add-layer-flux.service';

export type BuildFieldInterventionContextConfigParams = {
    canManageFieldInterventionContextSelection: boolean;
    projectContainsLayerWithSameName: boolean;
};

export enum FieldInterventionContextState {
    EXCLUDED = 'excluded',
    INCLUDED = 'included',
    INHERITED = 'inherited',
}

export type FieldInterventionContextConfig = {
    disabled: boolean;
    state: FieldInterventionContextState;
    tooltip: string | null;
};

export type InitializeFieldInterventionContextStateParams = {
    canManageFieldInterventionContextSelection: boolean;
    canShowFieldInterventionColumn: boolean;
    layersInfo: Record<string, CreateLayerDtoWithoutProject>;
};

export type TableInfo = {
    hasGeom: boolean | undefined;
    id: string;
    tableName: string;
};
export interface LayersPresetsExtended extends LayerPreset {
    tableName: string;
}

export enum AddLayerSelectionType {
    FLUX = 'flux',
    INTERSTANDARD = 'interstandard',
    REFERENCE_DATA = 'referenceData',
    TABLE = 'table',
}

type NonFluxAddLayerSelectionType =
    AddLayerSelectionType.TABLE | AddLayerSelectionType.REFERENCE_DATA | AddLayerSelectionType.INTERSTANDARD;

export type FluxLayerType = UrlSourceEnum.WFS | UrlSourceEnum.WMS;

@Injectable({
    providedIn: 'root',
})
export class AddLayerService {
    public readonly addLayerFluxService = inject(AddLayerFluxService);

    public readonly availableSourceLayers = this.addLayerFluxService.availableSourceLayers;
    public readonly depositFolderByStandard = signal<Record<string, DepositFolder[]> | undefined>(undefined);
    public readonly depositFolderHasDeposits = signal(false);
    public readonly depositFolderOptions = computed(() =>
        (this.depositFolders() ?? []).reduce((acc: SelectOption<DepositFolder>[], depositFolder) => {
            return [...acc, { label: depositFolder.name, state: CheckboxState.UNCHECKED, value: depositFolder }];
        }, []),
    );
    public readonly depositFolders = signal<DepositFolder[] | undefined>(undefined);
    public readonly depositOptions = signal<SelectOption<string>[]>([]);
    public readonly depositStandard = signal<string | undefined>(undefined);
    public readonly existingProjectLayerNames = signal<Set<string>>(new Set());
    public readonly fieldInterventionContextByLayerId = signal<Record<string, FieldInterventionContextConfig>>({});
    public readonly firstInterstandardDepositFolderOptions = signal<SelectOption<DepositFolder>[]>([]);
    public readonly geomLayersInfo: Signal<Record<string, CreateLayerDtoWithoutProject>> = computed(() => {
        const layersEntries = Object.entries(this.layersInfo() ?? {});

        return this.getLayersByGeom(layersEntries, true);
    });
    public readonly interstandards = signal<Interstandard[]>([]);
    public readonly isDepositFolderLoading = signal<boolean>(false);
    public readonly isLayerInfoLoading = signal<boolean>(false);
    public readonly isLockVersion = signal<boolean>(false);
    public readonly layerCreatedCount = signal<number>(0);
    public layerFormRecord?: LayerFormRecord = undefined;
    public readonly layersInfo = signal<Record<string, CreateLayerDtoWithoutProject> | undefined>(undefined);
    public readonly layersPresetsExtended = signal<LayersPresetsExtended[]>([]);
    public readonly layerToCreateCount: Signal<number> = computed(
        () => Object.keys(this.geomLayersInfo()).length + Object.keys(this.noGeomLayersInfo()).length,
    );
    public readonly linkedStandards = signal<Standard[]>([]);
    public readonly noGeomLayersInfo: Signal<Record<string, CreateLayerDtoWithoutProject>> = computed(() => {
        const layersEntries = Object.entries(this.layersInfo() ?? {});

        return this.getLayersByGeom(layersEntries, false);
    });
    public readonly projectHasDepositFolder = computed(() => !!this.depositFolderOptions().length);
    public readonly resolvedFluxLayerInfoByTableId = this.addLayerFluxService.resolvedFluxLayerInfoByTableId;
    public readonly secondInterstandardDepositFolderOptions = signal<SelectOption<DepositFolder>[]>([]);
    public readonly selectedDeposit = signal<string | undefined>(undefined);
    public readonly selectedDepositFolder = signal<DepositFolder | undefined>(undefined);
    public readonly selectedDepositVersionAndName = signal<string>('');
    public readonly selectedFirstInterstandardDepositFolder = signal<DepositFolder | undefined>(undefined);
    public readonly selectedFluxType = this.addLayerFluxService.selectedFluxType;
    public readonly selectedSecondInterstandardDepositFolder = signal<DepositFolder | undefined>(undefined);
    public readonly selectedServiceCatalogs = this.addLayerFluxService.selectedServiceCatalogs;
    public readonly selectedTablesInfo = signal<TableInfo[]>([]);
    public readonly serviceCatalogResolutionErrors = this.addLayerFluxService.serviceCatalogResolutionErrors;
    public readonly tableOptions = signal<SelectOption<TableInfo>[]>([]);

    private readonly fieldInterventionPermissionService = inject(FieldInterventionPermissionService);
    private readonly layerCreationFormService = inject(LayerCreationFormService);
    private readonly layerPresetsService = inject(LayerPresetsService);
    private readonly layersService = inject(LayersService);
    private readonly projectBloc = inject(ProjectBloc);
    private readonly tablesService = inject(TablesService);
    private readonly translocoService = inject(TranslocoService);
    private readonly urlSourceService = inject(UrlSourceService);

    public constructor() {
        //this effect is used to reset data off the third step: selectedTable
        //in relation to different data that we select in the second step
        //in the future I will add selectedInterstandard and selectedFlux to reset selectedTable
        effect(() => {
            this.selectedDeposit();
            this.selectedFirstInterstandardDepositFolder();
            this.selectedSecondInterstandardDepositFolder();
            this.selectedTablesInfo.set([]);
            this.layersInfo.set(undefined);
            this.urlSourceService.wfsExtension.set(null);
        });
    }

    public get sourceUrlControl(): FormControl<string> | undefined {
        return this.addLayerFluxService.sourceUrlControl;
    }

    public set sourceUrlControl(sourceUrlControl: FormControl<string> | undefined) {
        this.addLayerFluxService.sourceUrlControl = sourceUrlControl;
    }

    public canManageFieldInterventionContextSelection(hasFieldInterventions: boolean): boolean {
        if (!hasFieldInterventions) {
            return false;
        }

        return this.fieldInterventionPermissionService.canManageFieldIntervention();
    }

    public canShowFieldInterventionColumn(hasFieldInterventions: boolean): boolean {
        if (!hasFieldInterventions) {
            return false;
        }

        return (
            this.fieldInterventionPermissionService.canReadFieldIntervention() ||
            this.fieldInterventionPermissionService.canManageFieldIntervention()
        );
    }

    public async createLayers(): Promise<Layer[]> {
        this.layerCreatedCount.set(0);
        let layers: CreateLayerDtoWithoutProject[] = [];

        if (this.layerFormRecord) {
            layers = Object.entries(this.geomLayersInfo()!).map(([uniqueId, layer]): CreateLayerDtoWithoutProject => {
                return {
                    ...layer,
                    ...this.layerFormRecord!.controls[uniqueId].value,
                    addToFieldInterventionContext: this.getAddToFieldInterventionContext(uniqueId),
                };
            });

            const noGeomLayer = Object.entries(this.noGeomLayersInfo()!).map(
                ([uniqueId, layer]): CreateLayerDtoWithoutProject => {
                    return {
                        ...layer,
                        ...this.layerFormRecord!.controls[uniqueId].value,
                    };
                },
            );

            layers.push(...noGeomLayer);
        }

        const newLayers: Layer[] = [];

        for (const layer of layers) {
            newLayers.push(
                await firstValueFrom(
                    this.layersService.layerControllerPost({
                        createLayerDto: {
                            ...layer,
                            project: this.projectBloc.value()!.id,
                        },
                    }),
                ),
            );
            this.layerCreatedCount.set(this.layerCreatedCount() + 1);
        }

        return newLayers;
    }

    public getDefaultListValue<T extends { id: string }>(
        options: SelectOption<T>[],
        selectedValues: T[],
    ): SelectOption<T>[] {
        return options.filter((option) => {
            return selectedValues.map((selectedValue: T) => selectedValue.id).includes(option.value.id);
        });
    }

    public getDefaultValue<T>(options: SelectOption<T>[], selectedValue: T | undefined): SelectOption<T>[] {
        return options.filter((option) => {
            return selectedValue === option.value;
        });
    }

    public groupDepositsByStandard(): GroupBucket<DepositFolder> | null {
        if (!this.depositFolders()?.length) {
            return null;
        }

        return this.depositFolders()!
            .filter((depositFolder: DepositFolder) => !depositFolder.standard?.isReferenceData)
            .reduce((acc: GroupBucket<DepositFolder>, depositFolder: DepositFolder) => {
                const depositFolderOption = {
                    label: depositFolder.name,
                    state: CheckboxState.UNCHECKED,
                    value: depositFolder,
                };

                const groupName = depositFolder.standard?.name ?? NO_GROUP;

                if (!acc[groupName]) {
                    acc[groupName] = [];
                }

                acc[groupName].push(depositFolderOption);

                return acc;
            }, {});
    }

    public initializeLayerInfo(layerType: AddLayerSelectionType, hasFieldInterventions: boolean): void {
        this.isLayerInfoLoading.set(true);
        let toRemoveTableInfo: string[] = [];

        if (this.layersInfo()) {
            toRemoveTableInfo = Object.keys(this.layersInfo()!).filter(
                (tableInfoId: string) =>
                    !this.selectedTablesInfo()
                        .map((tableInfo: TableInfo) => tableInfo.id)
                        .includes(tableInfoId),
            );
        }

        const newLayersInfo = this.selectedTablesInfo().reduce(
            (
                acc: Record<string, CreateLayerDtoWithoutProject>,
                tableInfo: TableInfo,
            ): Record<string, CreateLayerDtoWithoutProject> => {
                const createLayerDto = this.getLayerDto(layerType, tableInfo);

                return { ...acc, [tableInfo.id]: createLayerDto };
            },
            {},
        );

        this.layerFormRecord = this.layerCreationFormService.createFormWithUniqueId(
            newLayersInfo,
            this.layerFormRecord,
        );

        const mergedMap = new Map<string, CreateLayerDtoWithoutProject>();

        Object.entries(newLayersInfo).forEach(([uniqueId, value]) => {
            mergedMap.set(uniqueId, value);
        });

        Object.entries(this.layersInfo() ?? {}).forEach(([uniqueId, value]) => {
            mergedMap.set(uniqueId, value);
        });

        toRemoveTableInfo.forEach((id) => {
            this.layerFormRecord!.removeControl(id);
            mergedMap.delete(id);
        });

        const mergedLayersInfo = Object.fromEntries(mergedMap);
        this.layersInfo.set(mergedLayersInfo);

        const canManageFieldInterventionContextSelection =
            this.canManageFieldInterventionContextSelection(hasFieldInterventions);
        const canShowFieldInterventionColumn = this.canShowFieldInterventionColumn(hasFieldInterventions);

        this.initializeFieldInterventionContextState({
            canManageFieldInterventionContextSelection,
            canShowFieldInterventionColumn,
            layersInfo: mergedLayersInfo,
        });

        this.isLayerInfoLoading.set(false);
    }

    public initSelectFirstInterstandardDepositFolder(): void {
        if (!this.depositFolderByStandard()) {
            return;
        }

        this.firstInterstandardDepositFolderOptions.set(
            Object.values(this.depositFolderByStandard()!)
                .flatMap((depositFolder) => depositFolder)
                .reduce((acc: SelectOption<DepositFolder>[], depositFolder) => {
                    return [
                        ...acc,
                        { label: depositFolder.name, state: CheckboxState.UNCHECKED, value: depositFolder },
                    ];
                }, []),
        );
    }

    public resetSelectedData(): void {
        this.selectedDepositFolder.set(undefined);
        this.selectedDeposit.set(undefined);
        this.selectedFirstInterstandardDepositFolder.set(undefined);
        this.linkedStandards.set([]);
        this.selectedSecondInterstandardDepositFolder.set(undefined);
        this.isLockVersion.set(false);
        this.tableOptions.set([]);
        this.selectedTablesInfo.set([]);
        this.layersInfo.set(undefined);
        this.addLayerFluxService.reset();
        this.layerFormRecord = undefined;
    }

    public async resolveSelectedServiceCatalogs(): Promise<boolean> {
        const { hasResolvedEntries, selectedTablesInfo } =
            await this.addLayerFluxService.resolveSelectedServiceCatalogs();

        this.selectedTablesInfo.set(selectedTablesInfo);

        return hasResolvedEntries;
    }

    public selectServiceCatalogs(serviceCatalogs: Parameters<AddLayerFluxService['selectServiceCatalogs']>[0]): void {
        this.selectedTablesInfo.set(this.addLayerFluxService.selectServiceCatalogs(serviceCatalogs));
    }

    public async setAllLayersPresets(): Promise<void> {
        this.layersPresetsExtended.set([]);
        const paginationParameters = { ...defaultPaginationParameters, take: paginationMaxTake };

        let hasNextPage = false;

        do {
            const paginatedLayerPresets = await firstValueFrom(
                this.layerPresetsService.layerPresetControllerGet({
                    standard: this.depositStandard(),
                    ...paginationParameters,
                }),
            );

            const paginatedTables = await firstValueFrom(
                this.tablesService.tableControllerGet({
                    tableIds: paginatedLayerPresets.items.map((layerPreset) => layerPreset.table),
                    ...paginationParameters,
                }),
            );

            const layerPresetsExtended: LayersPresetsExtended[] = paginatedLayerPresets.items.reduce(
                (acc: LayersPresetsExtended[], layerPreset: LayerPreset): LayersPresetsExtended[] => {
                    const table = paginatedTables.items.find((table) => table.id === layerPreset.table);

                    if (!table) {
                        return acc;
                    }

                    return [...acc, { ...layerPreset, tableName: table.name }];
                },
                [],
            );

            this.layersPresetsExtended.update((currentValue: LayersPresetsExtended[]) =>
                currentValue.concat(layerPresetsExtended),
            );
            hasNextPage = paginatedLayerPresets.hasNextPage;

            paginationParameters.page! += 1;
        } while (hasNextPage);
    }

    public setExistingProjectLayerNames(layerNames: string[]): void {
        this.existingProjectLayerNames.set(
            new Set(layerNames.map((layerName: string) => this.normalizeLayerName(layerName))),
        );
    }

    public toggleFieldInterventionContext(uniqueId: string, canManageFieldInterventionContextSelection: boolean): void {
        if (!canManageFieldInterventionContextSelection) {
            return;
        }

        const currentConfig = this.fieldInterventionContextByLayerId()[uniqueId];
        if (!currentConfig || currentConfig.disabled) {
            return;
        }

        const nextState =
            currentConfig.state === FieldInterventionContextState.INCLUDED
                ? FieldInterventionContextState.EXCLUDED
                : FieldInterventionContextState.INCLUDED;

        this.fieldInterventionContextByLayerId.update(
            (currentValue: Record<string, FieldInterventionContextConfig>) => ({
                ...currentValue,
                [uniqueId]: {
                    ...currentConfig,
                    state: nextState,
                    tooltip: this.getFieldInterventionContextTooltip(nextState),
                },
            }),
        );
    }

    public updateCurrentLayerBusinessIdentifier(businessIdentifier: string | null, uniqueId: string): void {
        if (!this.layerFormRecord) {
            return;
        }

        this.layerFormRecord.controls[uniqueId].controls.businessIdentifier.setValue(businessIdentifier);
    }

    public updateCurrentLayerLock(uniqueId: string): void {
        if (!this.layerFormRecord) {
            return;
        }

        this.layerFormRecord.controls[uniqueId].controls.versionLock.setValue(
            !this.layerFormRecord.controls[uniqueId].value.versionLock,
        );
    }

    public updateCurrentLayerVisibility(uniqueId: string): void {
        if (!this.layerFormRecord) {
            return;
        }

        this.layerFormRecord.controls[uniqueId].controls.isVisible.setValue(
            !this.layerFormRecord.controls[uniqueId].value.isVisible,
        );
    }

    private buildFieldInterventionContextConfig(
        params: BuildFieldInterventionContextConfigParams,
    ): FieldInterventionContextConfig {
        const { canManageFieldInterventionContextSelection, projectContainsLayerWithSameName } = params;

        if (projectContainsLayerWithSameName) {
            return {
                disabled: true,
                state: FieldInterventionContextState.INHERITED,
                tooltip: this.getFieldInterventionContextTooltip(FieldInterventionContextState.INHERITED),
            };
        }

        return {
            disabled: !canManageFieldInterventionContextSelection,
            state: FieldInterventionContextState.INCLUDED,
            tooltip: this.getFieldInterventionContextTooltip(FieldInterventionContextState.INCLUDED),
        };
    }

    private getAddToFieldInterventionContext(uniqueId: string): boolean | undefined {
        const config = this.fieldInterventionContextByLayerId()[uniqueId];
        if (!config) {
            return undefined;
        }

        switch (config.state) {
            case FieldInterventionContextState.INCLUDED:
                return true;
            case FieldInterventionContextState.EXCLUDED:
                return false;
            case FieldInterventionContextState.INHERITED:
                return undefined;
        }
    }

    private getFieldInterventionContextTooltip(state: FieldInterventionContextState): string {
        switch (state) {
            case FieldInterventionContextState.INHERITED:
                return this.translocoService.translate('fieldInterventionContextInheritedTooltip');
            case FieldInterventionContextState.EXCLUDED:
                return this.translocoService.translate('fieldInterventionContextExcludedTooltip');
            case FieldInterventionContextState.INCLUDED:
                return this.translocoService.translate('fieldInterventionContextIncludedTooltip');
            default:
                return '';
        }
    }

    private getLayerDto(layerType: AddLayerSelectionType, tableInfo: TableInfo): CreateLayerDtoWithoutProject {
        if (layerType === AddLayerSelectionType.FLUX) {
            return this.addLayerFluxService.buildFluxLayerDto(tableInfo, this.selectedDeposit(), this.isLockVersion());
        }

        let layerInfo: CreateLayerDtoWithoutProject = {
            businessIdentifier: null,
            deposit: this.selectedDeposit(),
            hasGeom: tableInfo.hasGeom,
            isVisible: true,
            name: tableInfo.tableName,
            order: 1,
            type: this.toLayerType(layerType),
            versionLock: this.isLockVersion(),
        };

        switch (layerType) {
            case AddLayerSelectionType.TABLE:
            case AddLayerSelectionType.REFERENCE_DATA:
                layerInfo = this.getLayerTypeTableInfo(tableInfo, layerInfo);
                break;
            case AddLayerSelectionType.INTERSTANDARD:
                layerInfo = this.getLayerTypeInterstandardInfo(tableInfo, layerInfo);
                break;
        }

        return layerInfo;
    }

    private getLayersByGeom(
        layersEntries: [string, CreateLayerDtoWithoutProject][],
        hasGeom: boolean,
    ): Record<string, CreateLayerDtoWithoutProject> {
        return layersEntries.reduce(
            (
                acc: Record<string, CreateLayerDtoWithoutProject>,
                [key, layer]: [string, CreateLayerDtoWithoutProject],
            ): Record<string, CreateLayerDtoWithoutProject> => {
                if ((layer.hasGeom ?? false) === hasGeom) {
                    acc[key] = layer;
                }

                return acc;
            },
            {},
        );
    }

    private getLayerTypeInterstandardInfo(
        tableInfo: TableInfo,
        layerInfo: CreateLayerDtoWithoutProject,
    ): CreateLayerDtoWithoutProject {
        const interstandard = this.interstandards().find((interstandard) => interstandard.id === tableInfo.id);

        if (!interstandard) {
            return layerInfo;
        }

        return {
            ...layerInfo,
            businessIdentifier: interstandard.businessIdentifier,
            interstandardDepositIds: [
                this.selectedFirstInterstandardDepositFolder()!.deposit!.id,
                this.selectedSecondInterstandardDepositFolder()!.deposit!.id,
            ],
            interstandardId: interstandard.id,
            isVisible: interstandard.isVisible,
            layerStyle: interstandard.layerStyle,
            name: interstandard.name,
            order: interstandard.order,
        };
    }

    private getLayerTypeTableInfo(
        tableInfo: TableInfo,
        layerInfo: CreateLayerDtoWithoutProject,
    ): CreateLayerDtoWithoutProject {
        const selectedLayerPresetExtended = this.layersPresetsExtended().find(
            (layerPresetExtended) => layerPresetExtended.tableName === tableInfo.tableName,
        );

        if (selectedLayerPresetExtended) {
            layerInfo = {
                ...layerInfo,
                businessIdentifier: selectedLayerPresetExtended?.businessIdentifier ?? null,
                isVisible: selectedLayerPresetExtended.isVisible,
                layerStyle: selectedLayerPresetExtended.layerStyle,
                layerStyleConnectField: selectedLayerPresetExtended.layerStyleConnectField,
                layerTopicPresetId: selectedLayerPresetExtended.layerTopicPreset,
                name: selectedLayerPresetExtended.name,
                order: selectedLayerPresetExtended.order,
            };
        }

        return { ...layerInfo, tableName: tableInfo.tableName };
    }

    private initializeFieldInterventionContextState(params: InitializeFieldInterventionContextStateParams): void {
        const { canManageFieldInterventionContextSelection, canShowFieldInterventionColumn, layersInfo } = params;

        if (!canShowFieldInterventionColumn) {
            this.fieldInterventionContextByLayerId.set({});

            return;
        }

        const nextFieldInterventionContextByLayerId = Object.entries(layersInfo).reduce<
            Record<string, FieldInterventionContextConfig>
        >(
            (
                acc: Record<string, FieldInterventionContextConfig>,
                [uniqueId, layer]: [string, CreateLayerDtoWithoutProject],
            ) => {
                if (!this.isFieldInterventionContextEligibleLayer(layer)) {
                    return acc;
                }

                acc[uniqueId] = this.buildFieldInterventionContextConfig({
                    canManageFieldInterventionContextSelection,
                    projectContainsLayerWithSameName: this.projectContainsLayerWithSameName(layer.name),
                });

                return acc;
            },
            {},
        );

        this.fieldInterventionContextByLayerId.set(nextFieldInterventionContextByLayerId);
    }

    private isFieldInterventionContextEligibleLayer(layer: CreateLayerDtoWithoutProject): boolean {
        return layer.type === Layer.TypeEnum.TABLE && !!layer.hasGeom;
    }

    private normalizeLayerName(layerName: string | null | undefined): string {
        return (layerName ?? '').trim().toLowerCase();
    }

    private projectContainsLayerWithSameName(layerName: string): boolean {
        return this.existingProjectLayerNames().has(this.normalizeLayerName(layerName));
    }

    private toLayerType(layerType: NonFluxAddLayerSelectionType): Layer.TypeEnum {
        switch (layerType) {
            case AddLayerSelectionType.TABLE:
                return Layer.TypeEnum.TABLE;
            case AddLayerSelectionType.REFERENCE_DATA:
                return Layer.TypeEnum.REFERENCE_DATA;
            case AddLayerSelectionType.INTERSTANDARD:
                return Layer.TypeEnum.INTERSTANDARD;
        }

        throw new Error(`Unsupported add layer selection type: ${layerType}`);
    }
}
